// Draws a branded NumberSense milestone card onto a <canvas>.
// ratio: "square" (1080x1080) or "portrait" (1080x1350).

const BRAND = "#1E6B52";
const BRAND_DARK = "#17543f";
const CREAM = "#FAF8F5";
const TEXT = "#1F2824";
const ACCENT = "#D96B43";
const LIGHT = "#E5F3E8";

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapLines(ctx, text, maxWidth) {
  const words = (text || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function drawMilestone(canvas, data, ratio = "square") {
  const W = 1080;
  const H = ratio === "portrait" ? 1350 : 1080;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // background
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  // outer panel
  const pad = 70;
  const panelX = pad, panelY = pad, panelW = W - pad * 2, panelH = H - pad * 2;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, panelX, panelY, panelW, panelH, 48);
  ctx.fill();
  ctx.strokeStyle = "#ECE4D8";
  ctx.lineWidth = 2;
  ctx.stroke();

  // top brand bar
  const barH = 150;
  ctx.save();
  roundRect(ctx, panelX, panelY, panelW, panelH, 48);
  ctx.clip();
  const grad = ctx.createLinearGradient(panelX, panelY, panelX + panelW, panelY + barH);
  grad.addColorStop(0, BRAND);
  grad.addColorStop(1, BRAND_DARK);
  ctx.fillStyle = grad;
  ctx.fillRect(panelX, panelY, panelW, barH);
  ctx.restore();

  // logo monogram
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  roundRect(ctx, panelX + 48, panelY + 40, 70, 70, 18);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 44px Georgia, serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("N", panelX + 83, panelY + 77);
  ctx.textAlign = "left";
  ctx.font = "800 40px Arial, sans-serif";
  ctx.fillText(data.app_name || "NumberSense", panelX + 140, panelY + 70);
  ctx.font = "500 24px Arial, sans-serif";
  ctx.fillStyle = "#CDE6D8";
  ctx.fillText("Math that clicks", panelX + 140, panelY + 106);

  const cx = W / 2;
  let y = panelY + barH + (ratio === "portrait" ? 150 : 110);

  // celebration medal circle
  const r = 86;
  ctx.beginPath();
  ctx.arc(cx, y, r, 0, Math.PI * 2);
  ctx.fillStyle = LIGHT;
  ctx.fill();
  ctx.font = "700 86px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("🏆", cx, y + 6);
  y += r + (ratio === "portrait" ? 110 : 80);

  // "{name} just leveled up!"
  ctx.fillStyle = ACCENT;
  ctx.font = "700 40px Arial, sans-serif";
  ctx.fillText(`${data.first_name} just leveled up!`, cx, y);
  y += ratio === "portrait" ? 90 : 72;

  // title (wrapped, bold)
  ctx.fillStyle = TEXT;
  ctx.font = "800 64px Arial, sans-serif";
  const titleLines = wrapLines(ctx, data.title, panelW - 160);
  for (const line of titleLines) {
    ctx.fillText(line, cx, y);
    y += 78;
  }
  y += 16;

  // subtitle (wrapped)
  if (data.subtitle) {
    ctx.fillStyle = "#5B6963";
    ctx.font = "400 34px Arial, sans-serif";
    const subLines = wrapLines(ctx, data.subtitle, panelW - 200);
    for (const line of subLines) {
      ctx.fillText(line, cx, y);
      y += 48;
    }
  }

  // grade pill
  y += 24;
  const pillText = data.grade_label || "";
  if (pillText) {
    ctx.font = "700 30px Arial, sans-serif";
    const tw = ctx.measureText(pillText).width;
    const pw = tw + 60, ph = 56;
    ctx.fillStyle = LIGHT;
    roundRect(ctx, cx - pw / 2, y, pw, ph, 28);
    ctx.fill();
    ctx.fillStyle = BRAND;
    ctx.fillText(pillText, cx, y + ph / 2 + 2);
  }

  // footer tagline
  ctx.fillStyle = "#87968F";
  ctx.font = "500 28px Arial, sans-serif";
  ctx.fillText("See where your child stands — free 5-minute check", cx, panelY + panelH - 60);

  ctx.textAlign = "left";
  return canvas;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}
